"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeSampleExplorerViewState = exports.normalizeSampleExplorerViewColumns = exports.isSampleExplorerViewReportAggregation = exports.isSampleExplorerViewFilterLogic = exports.SampleExplorerViewWarningCode = void 0;
const object_1 = require("../../utils/object");
var SampleExplorerViewWarningCode;
(function (SampleExplorerViewWarningCode) {
    SampleExplorerViewWarningCode["MissingFormatRoute"] = "missing_format_route";
})(SampleExplorerViewWarningCode || (exports.SampleExplorerViewWarningCode = SampleExplorerViewWarningCode = {}));
const isStringArray = (value) => Array.isArray(value) && value.every((entry) => typeof entry === "string");
const isScalarFilterValue = (value) => typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean";
const isNullableScalarFilterValue = (value) => value === null || isScalarFilterValue(value);
const isSampleExplorerViewFilterLogic = (value) => value === "AND" || value === "OR";
exports.isSampleExplorerViewFilterLogic = isSampleExplorerViewFilterLogic;
const isSampleExplorerViewReportAggregation = (value) => value === "max" || value === "avg" || value === "sum" || value === "min";
exports.isSampleExplorerViewReportAggregation = isSampleExplorerViewReportAggregation;
const normalizeArray = (value, itemNormalizer) => {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.flatMap((entry) => {
        const parsedEntry = itemNormalizer(entry);
        return parsedEntry ? [parsedEntry] : [];
    });
};
const normalizeSampleExplorerViewSorting = (value) => normalizeArray(value, (entry) => {
    if (!(0, object_1.isPlainObject)(entry) ||
        typeof entry.id !== "string" ||
        typeof entry.desc !== "boolean") {
        return null;
    }
    return { id: entry.id, desc: entry.desc };
});
const normalizeSampleExplorerViewColumns = (value) => {
    if (!(0, object_1.isPlainObject)(value)) {
        return undefined;
    }
    const visibleFieldNames = isStringArray(value.visibleFieldNames)
        ? value.visibleFieldNames
        : [];
    const orderedFieldNames = isStringArray(value.orderedFieldNames)
        ? value.orderedFieldNames
        : visibleFieldNames;
    if (visibleFieldNames.length === 0 &&
        orderedFieldNames.length === 0 &&
        !isStringArray(value.visibleFieldNames) &&
        !isStringArray(value.orderedFieldNames)) {
        return undefined;
    }
    return {
        visibleFieldNames,
        orderedFieldNames,
    };
};
exports.normalizeSampleExplorerViewColumns = normalizeSampleExplorerViewColumns;
const normalizeSampleExplorerViewMetadataFilters = (value) => normalizeArray(value, (entry) => {
    if (!(0, object_1.isPlainObject)(entry) ||
        typeof entry.field !== "string" ||
        typeof entry.operator !== "string" ||
        !isScalarFilterValue(entry.value)) {
        return null;
    }
    return {
        field: entry.field,
        operator: entry.operator,
        value: entry.value,
    };
});
const normalizeSampleExplorerViewPipelineFilters = (value) => normalizeArray(value, (entry) => {
    if (!(0, object_1.isPlainObject)(entry)) {
        return null;
    }
    if (entry.field !== "pipelineId" &&
        entry.field !== "pipelineName" &&
        entry.field !== "pipelineVersion" &&
        entry.field !== "runName" &&
        entry.field !== "status") {
        return null;
    }
    if (entry.operator !== "eq" && entry.operator !== "like") {
        return null;
    }
    if (typeof entry.value !== "string" && typeof entry.value !== "number") {
        return null;
    }
    return {
        field: entry.field,
        operator: entry.operator,
        value: entry.value,
    };
});
const normalizeSampleExplorerViewReportFieldConfig = (value) => {
    if (!(0, object_1.isPlainObject)(value) ||
        !(0, exports.isSampleExplorerViewReportAggregation)(value.aggregation)) {
        return null;
    }
    return {
        aggregation: value.aggregation,
    };
};
const normalizeSampleExplorerViewReportFields = (value) => {
    if (!(0, object_1.isPlainObject)(value)) {
        return {};
    }
    return Object.entries(value).reduce((acc, [key, config]) => {
        const parsedConfig = normalizeSampleExplorerViewReportFieldConfig(config);
        if (parsedConfig) {
            acc[key] = parsedConfig;
        }
        return acc;
    }, {});
};
const normalizeSampleExplorerViewReportFilterClause = (value) => {
    if (!(0, object_1.isPlainObject)(value)) {
        return null;
    }
    const entries = Object.entries(value);
    if (entries.length !== 1) {
        return null;
    }
    const [fieldName, fieldConfig] = entries[0];
    if (!(0, object_1.isPlainObject)(fieldConfig)) {
        return null;
    }
    const operators = Object.entries(fieldConfig);
    if (operators.length !== 1) {
        return null;
    }
    const [operator, operatorValue] = operators[0];
    if (!isNullableScalarFilterValue(operatorValue)) {
        return null;
    }
    return {
        [fieldName]: {
            [operator]: operatorValue,
        },
    };
};
const normalizeSampleExplorerViewReportFilterClauses = (value) => normalizeArray(value, normalizeSampleExplorerViewReportFilterClause);
const normalizeSampleExplorerViewReportFilters = (value) => normalizeArray(value, (entry) => {
    if (!(0, object_1.isPlainObject)(entry) || typeof entry.reportRelativePath !== "string") {
        return null;
    }
    const fields = normalizeSampleExplorerViewReportFields(entry.fields);
    const filterConfig = (0, object_1.isPlainObject)(entry.filter) ? entry.filter : undefined;
    const and = filterConfig
        ? normalizeSampleExplorerViewReportFilterClauses(filterConfig.and)
        : [];
    const or = filterConfig
        ? normalizeSampleExplorerViewReportFilterClauses(filterConfig.or)
        : [];
    const filter = and.length > 0 ? { and } : or.length > 0 ? { or } : undefined;
    return {
        reportRelativePath: entry.reportRelativePath,
        fields,
        filter,
    };
});
const normalizeSampleExplorerViewState = (value) => {
    const parsed = (0, object_1.jsonParseReturnObject)(value);
    const metadata = (0, object_1.isPlainObject)(parsed.metadata) ? parsed.metadata : {};
    const pipeline = (0, object_1.isPlainObject)(parsed.pipeline) ? parsed.pipeline : {};
    const reports = (0, object_1.isPlainObject)(parsed.reports) ? parsed.reports : {};
    const viewState = {
        version: 1,
        mode: parsed.mode === "advanced" ? "advanced" : "simple",
        simpleQuery: typeof parsed.simpleQuery === "string" ? parsed.simpleQuery : "",
        metadata: {
            logic: (0, exports.isSampleExplorerViewFilterLogic)(metadata.logic)
                ? metadata.logic
                : "OR",
            filters: normalizeSampleExplorerViewMetadataFilters(metadata.filters),
        },
        pipeline: {
            logic: (0, exports.isSampleExplorerViewFilterLogic)(pipeline.logic)
                ? pipeline.logic
                : "OR",
            filters: normalizeSampleExplorerViewPipelineFilters(pipeline.filters),
        },
        reports: {
            logic: (0, exports.isSampleExplorerViewFilterLogic)(reports.logic)
                ? reports.logic
                : "OR",
            filters: normalizeSampleExplorerViewReportFilters(reports.filters),
        },
        sorting: normalizeSampleExplorerViewSorting(parsed.sorting),
    };
    const columns = (0, exports.normalizeSampleExplorerViewColumns)(parsed.columns);
    if (columns) {
        viewState.columns = columns;
    }
    if (typeof parsed.formatRoute === "string") {
        viewState.formatRoute = parsed.formatRoute;
    }
    if ((0, object_1.isPlainObject)(parsed.graphs)) {
        viewState.graphs = parsed.graphs;
    }
    return viewState;
};
exports.normalizeSampleExplorerViewState = normalizeSampleExplorerViewState;
